import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { IconButton } from 'components/buttons/IconButton';
import { HasOpenState } from 'hooks/HasOpenState';
import { DisplayFilter } from '@decisions/server/DecisionsFramework/Design/Flow';
import {
  setRuleDisplayFilter,
  getRuleDisplayFilter,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import './TruthTableFilter.less';
import { Callout } from 'components/layout/Callout';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';
import { IRootGroupModel } from '../RuleDesigner/models/RootGroupModel';
import { getHeaders, hasMergedCells } from './TruthTableUtils';

interface ITruthTableFilterProps extends React.HTMLProps<{}> {
  rootModel: IRootGroupModel;
  isFilterApplied: boolean;
}

const getButtonClassName = (isFilterApplied: boolean, isOpen: boolean) =>
  classNames('tt-table__filter-button', {
    'tt-table__filter-button--selected': isFilterApplied,
    'tt-table__filter-button--open': isOpen,
  });

/**
 *  Component allows to filter and to sort truth table
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TruthTableFilter({ isFilterApplied, rootModel }: ITruthTableFilterProps) {
  const { isOpen, setClosed, setOpen } = HasOpenState();
  const filterLayerRef = useRef<any | null>(null);
  const [filter, setFilter] = useState<DisplayFilter>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const layerContainerRef = useRef<HTMLElement>(null);
  const [isApplied, setApplied] = useState(false);
  const cannotFilter = hasMergedCells(rootModel);

  useEffect(() => {
    setApplied(isFilterApplied);
  }, [isFilterApplied]);

  const onOpen = () => {
    getRuleDisplayFilter({ ruleSessionId: rootModel.RuleSessionId }).then(filter => {
      setFilter(filter);
      setOpen();
    });
  };

  const handleClose = () => {
    setClosed();
    filterLayerRef.current?.remove();
    filterLayerRef.current = null;
  };

  useDidUpdateEffect(() => {
    if (!DEV_SERVER) {
      filterLayerRef.current = new (window as any).$DP.Rules.Truthtable.TruthTableFilter({
        displayFilter: filter,
        columnNames: getCellsNames(rootModel),
        onApply: (result: DisplayFilter) => {
          setRuleDisplayFilter({
            displayFilter: result,
            ruleSessionId: rootModel.RuleSessionId,
          });
          handleClose();
          setApplied(!!result);
        },
        onReset: () => {
          setRuleDisplayFilter({
            ruleSessionId: rootModel.RuleSessionId,
            displayFilter: null as any,
          });

          handleClose();
          setApplied(false);
        },
        onOpen: setOpen,
        onClose: handleClose,
      });

      //TODO: Move TruthTable Filter to React Project
      layerContainerRef.current?.append(filterLayerRef.current.getView()[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div className="tt-table__filter-button-panel">
      <IconButton
        disabled={cannotFilter}
        title={cannotFilter ? "Sorting and Filtering is not available when there are merged cells" : "Sorting and Filtering"}
        ref={buttonRef}
        className={getButtonClassName(isApplied, isOpen)}
        onClick={onOpen}
        iconSize={'large'}
        iconClassName={'tt-table__filter-button-icon'}
      />
      <Callout
        ref={layerContainerRef}
        isOpen={isOpen}
        onClose={handleClose}
        sourceRef={buttonRef}
        showArrow={true}
        placement={'bottom-end'}
      >
        {null}
      </Callout>
    </div>
  );
}

function getCellsNames(model: IRootGroupModel) {
  const result = getHeaders(model).map(p => `${p.AnchorInfo} ${p.VerbInfo}`);
  result.push(...model.RuleDisplayData.DefaultResults.map(p => p.DisplayName));
  return result;
}
